import type { Tier, BillingCycle } from '../types';

type PlanLimits = {
  brands: number;
  channels: number;
};

type Pricing = Record<BillingCycle, number>;

export interface PlanDefinition {
  id: Tier;
  name: string;
  description: string;
  limits: PlanLimits;
  pricing: Pricing;
  features: string[];
  checklist: string[];
  highlight?: boolean;
}

export const DEFAULT_BILLING_CYCLE: BillingCycle = 'yearly';

export const PLAN_DEFINITIONS: Record<Tier, PlanDefinition> = {
  free: {
    id: 'free',
    name: 'Free',
    description: 'Launch your social presence.',
    limits: {
      brands: 1,
      channels: 1,
    },
    pricing: {
      monthly: 0,
      yearly: 0,
    },
    features: ['1 Brand', '1 Channel', 'Manual Scheduling'],
    checklist: ['1 Workspace (Brand)', '1 Social Account', 'Manual Publishing'],
  },
  standard: {
    id: 'standard',
    name: 'Standard',
    description: 'Grow your reach.',
    limits: {
      brands: 1,
      channels: 3,
    },
    pricing: {
      monthly: 12,
      yearly: 10,
    },
    features: ['1 Brand', '3 Channels', 'Basic Analytics'],
    checklist: ['1 Workspace', '3 Social Accounts', 'Analytics Dashboard'],
  },
  professional: {
    id: 'professional',
    name: 'Professional',
    description: 'Scale for growing teams.',
    limits: {
      brands: 1,
      channels: 9,
    },
    pricing: {
      monthly: 25,
      yearly: 20,
    },
    features: ['1 Brand', '9 Channels', 'Advanced Analytics'],
    checklist: ['1 Workspace', '9 Social Accounts', 'Team Collaboration'],
    highlight: true,
  },
  premium: {
    id: 'premium',
    name: 'Premium',
    description: 'Maximum power.',
    limits: {
      brands: 1,
      channels: 13,
    },
    pricing: {
      monthly: 35,
      yearly: 30,
    },
    features: ['1 Brand', '13 Channels', 'Priority Support'],
    checklist: ['1 Workspace', '13 Social Accounts', 'Priority Support'],
  },
};

export const PLAN_LIMITS: Record<Tier, PlanLimits> = {
  free: PLAN_DEFINITIONS.free.limits,
  standard: PLAN_DEFINITIONS.standard.limits,
  professional: PLAN_DEFINITIONS.professional.limits,
  premium: PLAN_DEFINITIONS.premium.limits,
};

export const getPlanPrice = (plan: Tier, billingCycle: BillingCycle) =>
  PLAN_DEFINITIONS[plan].pricing[billingCycle];

export const getPlanLimits = (plan: Tier): PlanLimits => PLAN_LIMITS[plan];

export const getPlanDefinition = (plan: Tier): PlanDefinition => PLAN_DEFINITIONS[plan];

export const planHasFeature = (plan: Tier, feature: string): boolean => {
  const definition = getPlanDefinition(plan);
  return definition.features.includes(feature) || definition.checklist.includes(feature);
};
